import { useCallback, useState } from "react";
import { useCookies } from "react-cookie";
import { useStorage } from "./useStorage";
import { AppType, SignType } from "#/unique/domain";
import {
  signInWithAppleWeb,
  isAppleUserCancel,
  isApplePopupBlocked,
} from "#/auth/appleSignIn";
import { businessLogin, userLogin } from "~/services/auth";
import { useLoginHandlers } from "%/loginHandlers";
import { clearForceRelogin } from "#/auth/signIn";
import { useToastStore } from "&/toast";

/**
 * 웹 전용 애플 로그인 훅.
 *
 * 카카오·구글·네이버는 OAuth 리다이렉트 후 /oauth/{provider} 라우트에서 로그인을 마치지만,
 * 애플은 response_mode 가 form_post 라 정적 프론트 라우트로 콜백을 받을 수 없다.
 * 그래서 팝업(web_message)으로 identityToken 을 받아 같은 페이지에서 처리한다.
 *
 * 앱(MT_I / MT_A)은 네이티브 브릿지로 처리하므로 이 훅을 쓰지 않는다.
 */
export const useAppleLogin = () => {
  const [, setCookie] = useCookies(["email"]);
  const [, setSns] = useStorage<SignType>("local-sign-sns-type");
  const [, setSnsId] = useStorage<string>("local-sns-id");
  const { handleLoginSuccess, handleLoginFailure } = useLoginHandlers();
  const { add } = useToastStore();
  const [isPending, setIsPending] = useState(false);

  /**
   * ⚠️ 클릭 핸들러에서 다른 await 없이 곧바로 호출할 것 (팝업 차단 방지)
   */
  const loginWithApple = useCallback(async () => {
    const userType = import.meta.env.VITE_APP_TYPE as AppType;
    setIsPending(true);

    try {
      const { appleId, email } = await signInWithAppleWeb();

      console.log("[LOGIN-APPLE] 토큰 검증 성공", {
        appleId,
        hasEmail: !!email,
      });

      // 다른 SNS 콜백과 동일하게 저장 — 회원가입 폼이 local-sns-id 를 읽는다
      setSns("SNS_TYPE_APPLE");
      setSnsId(appleId);
      if (email) {
        setCookie("email", email, { secure: true, maxAge: 3600 });
      }

      const response = await (userType === "business"
        ? businessLogin({ snsId: appleId, cdSnsType: "SNS_TYPE_APPLE" })
        : userLogin({ snsId: appleId, cdSnsType: "SNS_TYPE_APPLE" }));

      if (response?.statusCode === 200) {
        clearForceRelogin();
        await handleLoginSuccess(userType, response);
      } else {
        // 201 이면 미가입 → handleLoginFailure 가 회원가입 화면으로 보낸다
        handleLoginFailure(
          userType,
          response?.statusCode || 500,
          "SNS_TYPE_APPLE",
        );
      }
    } catch (error) {
      if (isAppleUserCancel(error)) {
        console.log("[LOGIN-APPLE] 사용자가 팝업을 닫음");
        return;
      }
      console.error("[LOGIN-APPLE] 실패", error);
      if (isApplePopupBlocked(error)) {
        add("브라우저가 팝업을 차단했습니다. 팝업을 허용한 뒤 다시 시도해주세요.");
        return;
      }
      add("애플 로그인에 실패했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsPending(false);
    }
  }, [
    setSns,
    setSnsId,
    setCookie,
    handleLoginSuccess,
    handleLoginFailure,
    add,
  ]);

  return { loginWithApple, isPending };
};
